import { useState, useEffect, useRef, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import DownloadIcon from "../../resources/icons/download-01-white.svg";
import Toast from "../Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import DownloadFile from "../../service/DownloadFile";
import CfLoader from "../CfLoader/CfLoader";
import renderPdf from "../RenderPdf/RenderPdf";

export default function PrintModal({
  isOpen,
  toggleModal,
  svg_url,
  pdf_url,
  credential_id,
}) {
  const [currentView, setCurrentView] = useState("RGB");
  const [isSvgLoading, setIsSvgLoading] = useState(false);
  const pdfCanvasRef = useRef(null);

  function closeModal() {
    toggleModal(false);
    setCurrentView("RGB");
    setIsSvgLoading(false);
  }

  useEffect(() => {
    if (isOpen && currentView === "CMYK" && pdf_url) {
      renderPdf(pdf_url, pdfCanvasRef);
    }
  }, [currentView, isOpen, pdf_url]);

  useEffect(() => {
    if (isOpen && currentView === "RGB" && svg_url) {
      setIsSvgLoading(true);
      const img = new Image();
      img.onload = () => {
        setIsSvgLoading(false);
      };
      img.onerror = () => {
        setIsSvgLoading(false);
      };
      img.src = svg_url;
    }
  }, [currentView, isOpen, svg_url]);

  const handleDownload = async () => {
    if (currentView === "RGB") {
      // Download SVG as PNG
      await DownloadFile(EntityConstants.DOWNLOAD_SVG_AS_PNG, credential_id);
    } else {
      // Download PDF directly
      try {
        const response = await fetch(pdf_url);
        if (!response.ok) throw new Error("Failed to fetch PDF");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `certificate_${credential_id}.pdf`;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading PDF:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to download PDF",
        });

        // Fallback - open in new tab
        window.open(pdf_url, "_blank");
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform overflow-y-auto rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all relative">
                <button
                  type="button"
                  className="absolute text-xl top-2 right-3 text-cfSideBarDisabledText hover:text-cfViewAllTagText focus:outline-none"
                  onClick={closeModal}
                >
                  <span className="sr-only">Close</span>
                  &#10005;
                </button>

                <div className="border-b border-gray-200 mb-4 flex justify-center">
                  <nav className="flex -mb-px">
                    <button
                      type="button"
                      onClick={() => setCurrentView("RGB")}
                      className={`py-2 px-4 text-sm font-medium focus:outline-none ${
                        currentView === "RGB"
                          ? "border-b-2 border-cfButton text-cfButton"
                          : "text-cfViewAllTagText hover:text-cfCardTagText hover:border-cfSignInSignUpInputBorder border-b-2 border-transparent"
                      } ${
                        !svg_url ? "cursor-not-allowed opacity-50" : ""
                      } transition-colors duration-200`}
                      disabled={!svg_url}
                    >
                      RGB (Digital View)
                    </button>
                    <button
                      type="button"
                      onClick={() => pdf_url && setCurrentView("CMYK")}
                      className={`py-2 px-4 text-sm font-medium focus:outline-none ${
                        currentView === "CMYK"
                          ? "border-b-2 border-cfButton text-cfButton"
                          : "text-cfViewAllTagText hover:text-cfCardTagText hover:border-cfSignInSignUpInputBorder border-b-2 border-transparent"
                      } ${
                        !pdf_url ? "cursor-not-allowed opacity-50" : ""
                      } transition-colors duration-200`}
                      disabled={!pdf_url}
                    >
                      CMYK (Print View)
                    </button>
                  </nav>
                </div>

                <div className="mt-2 relative">
                  <div className="flex justify-center items-center h-[70vh]">
                    {currentView === "RGB" ? (
                      svg_url ? (
                        <div className="h-full w-full flex items-center justify-center">
                          {isSvgLoading ? (
                            <CfLoader />
                          ) : (
                            <>
                              <img
                                src={svg_url}
                                alt="Digital Certificate"
                                className="max-h-full max-w-full object-contain border rounded-md shadow-sm"
                                onLoad={() => setIsSvgLoading(false)}
                              />
                              <div className="absolute top-0 right-3">
                                <button
                                  onClick={handleDownload}
                                  className="flex items-center rounded-full gap-2 px-2 py-2 bg-cfButton text-cfSecondary hover:bg-cfButton"
                                >
                                  <img
                                    src={DownloadIcon}
                                    alt="Download"
                                    className="w-5 h-5"
                                  />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center bg-cfSearchContainer rounded-md">
                          <p className="text-cfViewAllTagText">
                            Digital version not available
                          </p>
                        </div>
                      )
                    ) : pdf_url ? (
                      <div className="h-full w-full flex items-center justify-center">
                        <canvas
                          ref={pdfCanvasRef}
                          className="max-w-full max-h-full"
                        />
                        <div className="absolute top-0 right-3">
                          <button
                            onClick={handleDownload}
                            className="flex items-center rounded-full gap-2 px-2 py-2 bg-cfButton text-cfSecondary hover:bg-cfButton"
                          >
                            <img
                              src={DownloadIcon}
                              alt="Download"
                              className="w-5 h-5"
                            />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center bg-cfSearchContainer rounded-md">
                        <p className="text-cfViewAllTagText">
                          Print version not available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
